import { Button, NumberInput, Stack, Text } from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { getCloudMetadata } from "../shared/getCurrentUser";
import { queryKeys, RestApi } from "../shared/RestApi";
import { NavBarContainer } from "./NavBarContainer";

declare const Stripe: (key: string) => {
  redirectToCheckout: (opts: {
    sessionId: string;
  }) => Promise<{ error?: { message: string } }>;
};

export function ComprarLicencias() {
  const { t } = useTranslation();
  const [licenseCount, setLicenseCount] = React.useState(1);
  const cloudInfo = getCloudMetadata();

  const {
    data: distributorInfo,
    isPending,
    isError,
  } = useQuery({
    queryKey: queryKeys.distributorInfo,
    queryFn: () => RestApi.getDistributorInfo(),
  });

  const checkoutMutation = useMutation({
    mutationFn: async (count: number) => {
      if (!distributorInfo) {
        throw new Error("Distributor info should be defined");
      }

      const sessionId = await RestApi.createDistributorStripeSession(count);
      const stripe = Stripe(cloudInfo.StripePublishableKey);
      const result = await stripe.redirectToCheckout({ sessionId });
      if (result.error) {
        console.error(result.error.message);
      }
    },
  });

  const updatePaymentInformation = (value: string | number) => {
    const parsed = typeof value === "number" ? value : parseInt(value, 10);
    if (Number.isNaN(parsed)) {
      return;
    }
    setLicenseCount(parsed);
  };

  if (isPending) {
    return (
      <NavBarContainer title={t("licenses.buyTitle")}>
        <Text>{t("licenses.buyLoading")}</Text>
      </NavBarContainer>
    );
  }

  if (isError || !distributorInfo) {
    return (
      <NavBarContainer title={t("licenses.buyTitle")}>
        <Text c="red">{t("licenses.buyLoadError")}</Text>
      </NavBarContainer>
    );
  }

  const costo = cloudInfo.PriceForDistributors * licenseCount;

  return (
    <NavBarContainer title={t("licenses.buyTitle")}>
      <Stack gap="md" maw={560}>
        <Text>
          {t("licenses.buyIntro", {
            price: cloudInfo.PriceForDistributors,
            recommended: cloudInfo.PriceRecommendedForDistributors,
          })}
        </Text>

        <NumberInput
          label={t("licenses.licenseCountLabel")}
          id="licenseCount"
          name="quantity"
          min={1}
          max={50}
          step={1}
          value={licenseCount}
          onChange={updatePaymentInformation}
        />

        {licenseCount > 0 && (
          <Stack gap="sm">
            <Text>
              {t("licenses.costSummary", {
                count: licenseCount,
                cost: costo,
              })}
            </Text>
            <Button
              color="green"
              loading={checkoutMutation.isPending}
              onClick={() => checkoutMutation.mutate(licenseCount)}
            >
              {t("licenses.buyButton", { count: licenseCount })}
            </Button>
          </Stack>
        )}
      </Stack>
    </NavBarContainer>
  );
}
